import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { queryKeys } from "~/api/queries";
import type { Abilities, Workspace } from "~/api/types";

export type PolicyTarget = string | { id: string; abilities?: Abilities } | null | undefined;

/**
 * Reading a permission the server never granted must be `false`, never `undefined` — a
 * typo'd ability name should hide a control, not render it by accident.
 */
export function denyAll(): Abilities {
  return sealed({});
}

function sealed(abilities: Abilities): Abilities {
  return new Proxy(abilities, {
    get: (target, key) => (typeof key === "string" ? (target[key] ?? false) : undefined),
    has: () => true,
  });
}

function findAbilitiesById(data: unknown, id: string, depth = 0): Abilities | null {
  if (!data || depth > 3) {
    return null;
  }
  if (Array.isArray(data)) {
    for (const entry of data) {
      const found = findAbilitiesById(entry, id, depth + 1);
      if (found) {
        return found;
      }
    }
    return null;
  }
  if (typeof data === "object") {
    const record = data as Record<string, unknown>;
    if (record.id === id && record.abilities && typeof record.abilities === "object") {
      return record.abilities as Abilities;
    }
  }
  return null;
}

/**
 * Resolve abilities for an entity. An object carries its own; a bare id is looked up across
 * whatever the query cache already holds, which is how a context menu on a list row can gate
 * itself without refetching the row.
 */
export function resolveAbilities(queryClient: QueryClient, target: PolicyTarget): Abilities {
  if (!target) {
    return denyAll();
  }
  if (typeof target !== "string") {
    return sealed(target.abilities ?? {});
  }
  for (const query of queryClient.getQueryCache().getAll()) {
    const found = findAbilitiesById(query.state.data, target);
    if (found) {
      return sealed(found);
    }
  }
  return denyAll();
}

/**
 * `const can = usePolicy(item); can.update && <RenameButton />`
 *
 * The UI asks; it never decides. No permission rule is encoded on this side of the wire.
 */
export function usePolicy(target?: PolicyTarget): Abilities {
  const queryClient = useQueryClient();
  return resolveAbilities(queryClient, target);
}

/**
 * Well-known id for "the workspace the viewer is currently in". Actions cannot know its
 * real id, and `ActionContext.abilities` is keyed by id, so the convention lives here.
 */
export const CURRENT_WORKSPACE = "workspace";

/** The id-keyed form the action system needs (`ctx.abilities("item-3").delete`). */
export function useAbilitiesLookup(): (entityId: string) => Abilities {
  const queryClient = useQueryClient();
  return useCallback(
    (entityId: string) => {
      if (entityId === CURRENT_WORKSPACE) {
        const workspace = queryClient.getQueryData<Workspace>(queryKeys.currentWorkspace);
        return resolveAbilities(queryClient, workspace ?? null);
      }
      return resolveAbilities(queryClient, entityId);
    },
    [queryClient],
  );
}
